import { farmWallClockToInstant } from '../../utils/farmTime'
import { parseCsvImportTable } from '../../utils/importTable'
import { findPlacementOnDay, type LotPlacement } from './lotPlacements'

// Findings #199 (back-fill for older lots): one CSV per facility, one line per
// unit, lot and day, like a farm notebook. Each line becomes the same records
// the log screens write (Conteo, Muestreo, Mortalidad, Alimento). Lots are
// never created here: a line whose lot does not exist in that unit on that day
// is an error that says what to do in Lotes first.

export type DailySheetColumn = 'date' | 'unit' | 'lot' | 'count' | 'method' | 'weight' | 'mortality' | 'feed' | 'notes'

/** Template header, in this order. */
export const DAILY_SHEET_COLUMNS: DailySheetColumn[] = ['date', 'unit', 'lot', 'count', 'method', 'weight', 'mortality', 'feed', 'notes']

const HEADER_ALIASES: Record<DailySheetColumn, string[]> = {
  date: ['fecha', 'dia', 'date'],
  unit: ['unidad', 'unidaddeproduccion', 'unidadproductiva', 'estanque', 'poza', 'unit'],
  lot: ['lote', 'lot'],
  count: ['conteo', 'stock', 'peces', 'stockdepeces', 'numerodepeces', 'count'],
  method: ['metodo', 'metododeconteo', 'method'],
  weight: ['pesopromediog', 'pesopromedio', 'peso', 'pesog', 'weight', 'weightg'],
  mortality: ['mortalidad', 'mortalidaddiaria', 'muertos', 'mortality'],
  feed: ['alimentokg', 'alimento', 'kgalimento', 'feed', 'feedkg'],
  notes: ['notas', 'nota', 'observaciones', 'comentarios', 'notes']
}

export const normalizeSheetHeader = (value: string): string => value
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '')

export type DailySheetIssue = { code: string; params?: Record<string, string> }

export type DailySheetRecordKind = 'count' | 'weight' | 'mortality' | 'feed'

export type DailySheetRecord =
  | { kind: 'count'; value: number; method: 'count' | 'estimation' }
  | { kind: 'weight'; value: number }
  | { kind: 'mortality'; value: number }
  | { kind: 'feed'; value: number }

export type DailySheetLine = {
  lineNumber: number
  raw: Partial<Record<DailySheetColumn, string>>
  day?: string
  unitId?: string
  unitName?: string
  lotId?: string
  lotKey?: string
  recordedAt?: string
  notes?: string
  records: DailySheetRecord[]
  errors: DailySheetIssue[]
  warnings: DailySheetIssue[]
}

export type DailySheetResult = {
  headerErrors: DailySheetIssue[]
  lines: DailySheetLine[]
  totals: Record<DailySheetRecordKind, number> & { okLines: number; errorLines: number }
}

export type DailySheetLot = {
  id: string
  lotKey: string
  productionUnitId?: string
}

export type DailySheetContext = {
  timeZone: string
  /** Farm today (YYYY-MM-DD): later days are refused. */
  today: string
  facilityName: string
  units: ReadonlyArray<{ id: string; name: string }>
  /** The facility's lots that are not deleted (any state). */
  lots: ReadonlyArray<DailySheetLot>
  placementsByLotId: ReadonlyMap<string, readonly LotPlacement[]>
  deletedLots: ReadonlyArray<{ lotKey: string; productionUnitId?: string }>
  /** `${kind}|${unitId}|${lotId}|${day}` of records already saved by the screens. */
  existingRecordKeys?: ReadonlySet<string>
}

const nameKey = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase()
const looseKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '')

const pad = (value: number) => String(value).padStart(2, '0')

/** "2026-09-09", "9/9/2026", "09-09-2026" or "09.09.2026" → "2026-09-09"; null when not a real day. */
export const parseSheetDay = (value: string): string | null => {
  const trimmed = value.trim()
  let year: number
  let month: number
  let day: number
  const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ].*)?$/)
  const dmy = trimmed.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/)
  if (iso) {
    year = Number(iso[1]); month = Number(iso[2]); day = Number(iso[3])
  } else if (dmy) {
    day = Number(dmy[1]); month = Number(dmy[2]); year = Number(dmy[3])
  } else {
    return null
  }
  const date = new Date(Date.UTC(year, month - 1, day))
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null
  return `${year}-${pad(month)}-${pad(day)}`
}

/** Whole numbers: "20428", "20 428", "20.428" or "20,428". */
export const parseSheetWholeNumber = (value: string): number | null => {
  const compact = value.trim().replace(/\s+/g, '')
  if (/^\d+$/.test(compact)) return Number(compact)
  if (/^\d{1,3}([.,]\d{3})+$/.test(compact)) return Number(compact.replace(/[.,]/g, ''))
  return null
}

/** Decimals: "0.35", "0,35", "1.234,5" or "1,234.5". */
export const parseSheetDecimal = (value: string): number | null => {
  const compact = value.trim().replace(/\s+/g, '')
  if (!/^\d[\d.,]*$/.test(compact)) return null
  const lastComma = compact.lastIndexOf(',')
  const lastDot = compact.lastIndexOf('.')
  const decimalSeparator = lastComma > lastDot ? ',' : '.'
  const thousands = decimalSeparator === ',' ? '.' : ','
  const normalized = compact.split(thousands).join('').replace(decimalSeparator, '.')
  if ((normalized.match(/\./g) ?? []).length > 1) return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

const parseMethod = (value: string): 'count' | 'estimation' | null => {
  const key = normalizeSheetHeader(value)
  if (!key || ['exacto', 'exacta', 'conteo', 'contado', 'count', 'exact'].includes(key)) return 'count'
  if (['estimacion', 'estimado', 'estimada', 'estimation', 'estimate'].includes(key)) return 'estimation'
  return null
}

/** The time a line's records get: midday on the farm, kept inside the lot's stay in that unit. */
const lineRecordedAt = (day: string, placement: LotPlacement, timeZone: string): string => {
  const noon = Date.parse(farmWallClockToInstant(`${day}T12:00`, timeZone) ?? `${day}T12:00:00Z`)
  let at = noon
  const earliest = placement.fromDay === day && placement.fromMs !== null ? placement.fromMs + 60_000 : null
  const latest = placement.toDay === day && placement.toMs !== null ? placement.toMs - 60_000 : null
  if (earliest !== null && at < earliest) at = earliest
  if (latest !== null && at > latest) at = latest
  if (earliest !== null && latest !== null && latest < earliest) at = Math.round((earliest + latest) / 2)
  return new Date(at).toISOString()
}

const formatDay = (day: string | null): string => (day ? `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)}` : '')

export const recordKey = (kind: DailySheetRecordKind, unitId: string, lotId: string, day: string): string => (
  `${kind}|${unitId}|${lotId}|${day}`
)

const resolveLot = (line: DailySheetLine, lotName: string, ctx: DailySheetContext): void => {
  const unitId = line.unitId as string
  const day = line.day as string
  const sameName = ctx.lots.filter((lot) => nameKey(lot.lotKey) === nameKey(lotName))
  const there = sameName
    .map((lot) => ({ lot, placement: findPlacementOnDay(ctx.placementsByLotId.get(lot.id) ?? [], unitId, day) }))
    .filter((entry): entry is { lot: DailySheetLot; placement: LotPlacement } => Boolean(entry.placement))

  if (there.length === 1) {
    line.lotId = there[0].lot.id
    line.lotKey = there[0].lot.lotKey
    line.recordedAt = lineRecordedAt(day, there[0].placement, ctx.timeZone)
    return
  }
  const params = { lot: lotName, unit: line.unitName ?? '', date: formatDay(day) }
  if (there.length > 1) {
    line.errors.push({ code: 'lotAmbiguous', params })
    return
  }
  if (ctx.deletedLots.some((lot) => nameKey(lot.lotKey) === nameKey(lotName) && lot.productionUnitId === unitId)) {
    line.errors.push({ code: 'lotDeleted', params })
    return
  }
  const stays = sameName.flatMap((lot) => (ctx.placementsByLotId.get(lot.id) ?? []).filter((placement) => placement.unitId === unitId))
  if (stays.length > 0) {
    const first = stays.reduce((a, b) => ((a.fromDay ?? '') <= (b.fromDay ?? '') ? a : b))
    if (first.fromDay && day < first.fromDay && stays.every((stay) => !stay.fromDay || day < stay.fromDay)) {
      line.errors.push({ code: 'beforeLotInUnit', params: { ...params, from: formatDay(first.fromDay) } })
    } else {
      const last = stays.find((stay) => stay.toDay === null) ?? stays[stays.length - 1]
      line.errors.push({ code: 'lotLeftUnit', params: { ...params, to: formatDay(last.toDay) } })
    }
    return
  }
  if (sameName.length > 0) {
    const unitNames = Array.from(new Set(sameName
      .map((lot) => ctx.units.find((unit) => unit.id === lot.productionUnitId)?.name)
      .filter((name): name is string => Boolean(name))))
    line.errors.push({ code: 'lotInOtherUnit', params: { ...params, units: unitNames.join(', ') } })
    return
  }
  const suggestion = ctx.lots.find((lot) => looseKey(lot.lotKey) === looseKey(lotName) && lot.productionUnitId === unitId)
  line.errors.push(suggestion
    ? { code: 'lotMissingSuggestion', params: { ...params, suggestion: suggestion.lotKey } }
    : { code: 'lotMissing', params: { ...params, facility: ctx.facilityName } })
}

export const parseDailySheet = (text: string, ctx: DailySheetContext): DailySheetResult => {
  const totals = { count: 0, weight: 0, mortality: 0, feed: 0, okLines: 0, errorLines: 0 }
  const table = parseCsvImportTable(text)
  if (!table) return { headerErrors: [{ code: 'empty' }], lines: [], totals }

  const columnIndex = new Map<DailySheetColumn, number>()
  table.headers.forEach((header, index) => {
    const key = normalizeSheetHeader(header)
    const column = DAILY_SHEET_COLUMNS.find((candidate) => HEADER_ALIASES[candidate].includes(key))
    if (column && !columnIndex.has(column)) columnIndex.set(column, index)
  })
  const headerErrors: DailySheetIssue[] = []
  for (const required of ['date', 'unit', 'lot'] as const) {
    if (!columnIndex.has(required)) headerErrors.push({ code: 'missingColumn', params: { column: required } })
  }
  if (!(['count', 'weight', 'mortality', 'feed'] as const).some((column) => columnIndex.has(column))) {
    headerErrors.push({ code: 'noValueColumns' })
  }
  if (headerErrors.length > 0) return { headerErrors, lines: [], totals }

  const unitByName = new Map(ctx.units.map((unit) => [nameKey(unit.name), unit] as const))
  const seen = new Map<string, number>()
  const lines: DailySheetLine[] = []

  for (const row of table.rows) {
    const raw: Partial<Record<DailySheetColumn, string>> = {}
    columnIndex.forEach((index, column) => {
      raw[column] = String(row.values[index] ?? '').trim()
    })
    const line: DailySheetLine = { lineNumber: row.sourceRowNumber, raw, records: [], errors: [], warnings: [] }
    lines.push(line)

    const day = raw.date ? parseSheetDay(raw.date) : null
    if (!raw.date) line.errors.push({ code: 'dateMissing' })
    else if (!day) line.errors.push({ code: 'dateInvalid', params: { value: raw.date } })
    else if (day > ctx.today) line.errors.push({ code: 'dateFuture', params: { date: formatDay(day) } })
    else line.day = day

    const unit = raw.unit ? unitByName.get(nameKey(raw.unit)) : undefined
    if (!raw.unit) line.errors.push({ code: 'unitMissing' })
    else if (!unit) line.errors.push({ code: 'unitUnknown', params: { unit: raw.unit, facility: ctx.facilityName } })
    else {
      line.unitId = unit.id
      line.unitName = unit.name
    }
    if (!raw.lot) line.errors.push({ code: 'lotNameMissing' })

    const method = parseMethod(raw.method ?? '')
    if (method === null) line.errors.push({ code: 'methodInvalid', params: { value: raw.method ?? '' } })
    if (raw.method && !raw.count) line.errors.push({ code: 'methodWithoutCount' })

    let hasValue = false
    if (raw.count) {
      hasValue = true
      const value = parseSheetWholeNumber(raw.count)
      if (value === null) line.errors.push({ code: 'countInvalid', params: { value: raw.count } })
      else line.records.push({ kind: 'count', value, method: method ?? 'count' })
    }
    if (raw.weight) {
      hasValue = true
      const value = parseSheetDecimal(raw.weight)
      if (value === null || value <= 0 || value > 20_000) line.errors.push({ code: 'weightInvalid', params: { value: raw.weight } })
      else line.records.push({ kind: 'weight', value })
    }
    if (raw.mortality) {
      hasValue = true
      const value = parseSheetWholeNumber(raw.mortality)
      if (value === null) line.errors.push({ code: 'mortalityInvalid', params: { value: raw.mortality } })
      else if (value > 0) line.records.push({ kind: 'mortality', value })
    }
    if (raw.feed) {
      hasValue = true
      const value = parseSheetDecimal(raw.feed)
      if (value === null || value > 100_000) line.errors.push({ code: 'feedInvalid', params: { value: raw.feed } })
      else if (value > 0) line.records.push({ kind: 'feed', value })
    }
    if (!hasValue) line.errors.push({ code: 'noValues' })
    if (raw.notes) line.notes = raw.notes

    if (line.day && line.unitId && raw.lot) {
      const key = `${line.unitId}|${nameKey(raw.lot)}|${line.day}`
      const previous = seen.get(key)
      if (previous !== undefined) {
        line.errors.push({ code: 'duplicateLine', params: { line: String(previous) } })
      } else {
        seen.set(key, line.lineNumber)
        resolveLot(line, raw.lot, ctx)
      }
    }

    if (line.lotId && line.unitId && line.day && ctx.existingRecordKeys) {
      const already = line.records
        .filter((record) => ctx.existingRecordKeys?.has(recordKey(record.kind, line.unitId as string, line.lotId as string, line.day as string)))
        .map((record) => record.kind)
      if (already.length > 0) line.warnings.push({ code: 'alreadyOnScreens', params: { kinds: already.join(',') } })
    }

    if (line.errors.length > 0) {
      totals.errorLines += 1
    } else {
      totals.okLines += 1
      line.records.forEach((record) => { totals[record.kind] += 1 })
    }
  }

  return { headerErrors, lines, totals }
}

/** The template a user downloads: header and two example lines. */
export const buildDailySheetTemplate = (headers: Record<DailySheetColumn, string>, examples: string[][]): string => {
  const escape = (value: string) => (/[",;\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value)
  return [DAILY_SHEET_COLUMNS.map((column) => headers[column]), ...examples]
    .map((cells) => cells.map(escape).join(','))
    .join('\r\n') + '\r\n'
}
